import { useEffect, useMemo, useState } from "react";
import { Loader2, Clock, Plus, X, Euro, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  priceRulesOverlap,
  hasFeature,
  type SubscriptionTier,
} from "@sportscanner/schemas";
import {
  Button,
  Checkbox,
  Switch,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@sportscanner/ui";
import ClubScopeShell from "@/components/club-scope-shell";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";
import { useSubscriptionActive, useMe } from "@/lib/me";
import { formatCourtLabel } from "@/components/booking/helpers";
import {
  generateSlotsFromPeriods,
  type SlotPeriod,
} from "./time-slot-generator";

// Day-of-time pricing rule (local, mutable). Days use 0=Sun…6=Sat to match the
// server's resolveSlotPrice. Price is a decimal string while editing.
interface PriceRule {
  days: number[];
  startTime: string;
  endTime: string;
  price: string;
}
type PriceRulesMap = Record<string, PriceRule[]>;

// Weekday columns rendered Monday-first (indices stay 0=Sun…6=Sat).
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const PRESET_DAYS: Record<string, number[]> = {
  weekdays: [1, 2, 3, 4, 5],
  weekends: [0, 6],
  everyday: [0, 1, 2, 3, 4, 5, 6],
};

function priceRulesEqual(a: PriceRule[], b: PriceRule[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (
      x.startTime !== y.startTime ||
      x.endTime !== y.endTime ||
      x.price !== y.price ||
      x.days.length !== y.days.length ||
      x.days.some((d, j) => d !== y.days[j])
    ) {
      return false;
    }
  }
  return true;
}

function cloneRules(rules: PriceRule[]): PriceRule[] {
  return rules.map((r) => ({ ...r, days: [...r.days] }));
}

interface TimeSlotsSport {
  id: string;
  name: string;
  sessionDurationMinutes: number;
}

interface ServerSlot {
  startTime: string;
  durationMinutes: number;
  memberOnly: boolean;
  isPrime: boolean;
  isMaintenance: boolean;
}

interface ServerPriceRule {
  days: number[];
  startTime: string;
  endTime: string;
  price: string | number;
}

interface ServerCourt {
  id: string;
  name: string;
  sportId: string;
  workingHoursStart: string | null;
  workingHoursEnd: string | null;
  pricePerSession: string;
  periods: SlotPeriod[] | null;
  priceRules: ServerPriceRule[] | null;
  slots: ServerSlot[];
}

interface TimeSlotsResponse {
  isFree: boolean;
  subscriptionTier: SubscriptionTier;
  allowMemberOnlySlots: boolean;
  allowPrimeTimeSlots: boolean;
  workingHoursStart: string;
  workingHoursEnd: string;
  sports: TimeSlotsSport[];
  courts: ServerCourt[];
}

interface LocalSlotState {
  memberOnly: boolean;
  isPrime: boolean;
  isMaintenance: boolean;
}

type SlotMap = Record<string, Record<string, LocalSlotState>>;
type PeriodsMap = Record<string, SlotPeriod[] | null>;

function emptyState(): LocalSlotState {
  return {
    memberOnly: false,
    isPrime: false,
    isMaintenance: false,
  };
}

function fromServerSlot(s: ServerSlot): LocalSlotState {
  return {
    memberOnly: s.memberOnly,
    isPrime: s.isPrime,
    isMaintenance: s.isMaintenance,
  };
}

function stateEquals(a: LocalSlotState, b: LocalSlotState): boolean {
  return (
    a.memberOnly === b.memberOnly &&
    a.isPrime === b.isPrime &&
    a.isMaintenance === b.isMaintenance
  );
}

function periodsEqual(a: SlotPeriod[] | null, b: SlotPeriod[] | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].startTime !== b[i].startTime ||
      a[i].durationMinutes !== b[i].durationMinutes
    ) {
      return false;
    }
  }
  return true;
}

// Chip visual state. Maintenance trumps everything else visually (the slot is
// closed so the member/prime distinction is irrelevant); price override layers
// on as a small euro badge.
type ChipBaseState = "off" | "memberOnly" | "prime" | "both" | "maintenance";
function chipBaseStateFor(s: LocalSlotState): ChipBaseState {
  if (s.isMaintenance) return "maintenance";
  if (s.memberOnly && s.isPrime) return "both";
  if (s.memberOnly) return "memberOnly";
  if (s.isPrime) return "prime";
  return "off";
}

const CHIP_CLASSES: Record<ChipBaseState, string> = {
  off: "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-300",
  memberOnly:
    "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100",
  prime: "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100",
  both: "bg-violet-50 border-violet-300 text-violet-800 hover:bg-violet-100",
  maintenance:
    "bg-neutral-100 border-neutral-300 text-neutral-500 line-through hover:bg-neutral-200",
};

export default function TimeSlotsPage() {
  const { t } = useTranslation();
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-neutral-800 mb-1">
          {t("timeSlots.title")}
        </h1>
        <p className="text-sm text-neutral-500">{t("timeSlots.subtitle")}</p>
      </div>

      <ClubScopeShell>
        {(clubId) => <TimeSlotsBody clubId={clubId} />}
      </ClubScopeShell>
    </div>
  );
}

function TimeSlotsBody({ clubId }: { clubId: string }) {
  const { t } = useTranslation();
  const subscriptionActive = useSubscriptionActive();
  const { refresh: refreshMe } = useMe();

  const query = useQuery({
    queryKey: ["admin", "time-slots", clubId],
    queryFn: () =>
      apiGet<TimeSlotsResponse>(`/api/admin/clubs/${clubId}/time-slots`),
    staleTime: 0,
  });

  const [periodsByCourt, setPeriodsByCourt] = useState<PeriodsMap>({});
  const [slotsByCourt, setSlotsByCourt] = useState<SlotMap>({});
  const [priceRulesByCourt, setPriceRulesByCourt] = useState<PriceRulesMap>({});
  // Club-level slot features, configured here (moved off the Features page).
  const [allowMemberOnly, setAllowMemberOnly] = useState(false);
  const [allowPrime, setAllowPrime] = useState(false);

  const original = useMemo(() => {
    if (!query.data) return null;
    const periods: PeriodsMap = {};
    const slots: SlotMap = {};
    const priceRules: PriceRulesMap = {};
    for (const c of query.data.courts) {
      periods[c.id] = c.periods;
      const courtMap: Record<string, LocalSlotState> = {};
      for (const s of c.slots) {
        courtMap[s.startTime] = fromServerSlot(s);
      }
      slots[c.id] = courtMap;
      priceRules[c.id] = (c.priceRules ?? []).map((r) => ({
        days: [...r.days],
        startTime: r.startTime,
        endTime: r.endTime,
        price: String(r.price),
      }));
    }
    return {
      periods,
      slots,
      priceRules,
      allowMemberOnly: query.data.allowMemberOnlySlots,
      allowPrime: query.data.allowPrimeTimeSlots,
    };
  }, [query.data]);

  useEffect(() => {
    if (!original) return;
    // Deep-copy so edits don't mutate the captured references.
    const periodsCopy: PeriodsMap = {};
    for (const [k, v] of Object.entries(original.periods)) {
      periodsCopy[k] = v === null ? null : v.map((p) => ({ ...p }));
    }
    setPeriodsByCourt(periodsCopy);
    const slotsCopy: SlotMap = {};
    for (const [k, v] of Object.entries(original.slots)) {
      slotsCopy[k] = { ...v };
    }
    setSlotsByCourt(slotsCopy);
    const priceRulesCopy: PriceRulesMap = {};
    for (const [k, v] of Object.entries(original.priceRules)) {
      priceRulesCopy[k] = cloneRules(v);
    }
    setPriceRulesByCourt(priceRulesCopy);
    setAllowMemberOnly(original.allowMemberOnly);
    setAllowPrime(original.allowPrime);
  }, [original]);

  const isDirty = useMemo(() => {
    if (!original) return false;
    for (const [courtId, periods] of Object.entries(periodsByCourt)) {
      if (!periodsEqual(periods, original.periods[courtId] ?? null)) {
        return true;
      }
    }
    for (const [courtId, courtSlots] of Object.entries(slotsByCourt)) {
      const orig = original.slots[courtId] ?? {};
      const startTimes = new Set([
        ...Object.keys(courtSlots),
        ...Object.keys(orig),
      ]);
      for (const t of startTimes) {
        const a = courtSlots[t] ?? emptyState();
        const b = orig[t] ?? emptyState();
        if (!stateEquals(a, b)) return true;
      }
    }
    for (const [courtId, rules] of Object.entries(priceRulesByCourt)) {
      if (!priceRulesEqual(rules, original.priceRules[courtId] ?? [])) {
        return true;
      }
    }
    if (
      allowMemberOnly !== original.allowMemberOnly ||
      allowPrime !== original.allowPrime
    ) {
      return true;
    }
    return false;
  }, [
    periodsByCourt,
    slotsByCourt,
    priceRulesByCourt,
    allowMemberOnly,
    allowPrime,
    original,
  ]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!query.data) return Promise.resolve();
      const courts: Record<
        string,
        {
          periods?: SlotPeriod[] | null;
          rules: LocalSlotRule[];
          priceRules: PriceRule[] | null;
        }
      > = {};
      for (const c of query.data.courts) {
        const periods = periodsByCourt[c.id];
        const courtSlots = slotsByCourt[c.id] ?? {};
        const rules: LocalSlotRule[] = [];
        for (const [startTime, s] of Object.entries(courtSlots)) {
          rules.push({
            startTime,
            memberOnly: s.memberOnly,
            isPrime: s.isPrime,
            isMaintenance: s.isMaintenance,
          });
        }
        // Drop incomplete rows (no days / blank or malformed price) so the
        // server's schema never rejects the whole save. Empty → null clears.
        const priceRules = (priceRulesByCourt[c.id] ?? []).filter(
          (r) => r.days.length > 0 && /^\d+(\.\d{1,2})?$/.test(r.price.trim()),
        );
        courts[c.id] = {
          periods: periods ?? null,
          rules,
          priceRules: priceRules.length > 0 ? priceRules : null,
        };
      }
      return apiPut(`/api/admin/clubs/${clubId}/time-slots`, {
        courts,
        allowMemberOnlySlots: allowMemberOnly,
        allowPrimeTimeSlots: allowPrime,
      });
    },
    onSuccess: () => {
      toast.success(t("timeSlots.savedToast"));
      void query.refetch();
      // Propagate the toggle change to the shared club context (sidebar,
      // Features limits visibility, etc.).
      void refreshMe();
    },
    onError: (err: unknown) => {
      const code = getApiErrorCode(err);
      toast.error(t(`errors.api.${code}`));
    },
  });

  function setPeriods(courtId: string, periods: SlotPeriod[] | null) {
    setPeriodsByCourt((prev) => ({ ...prev, [courtId]: periods }));
  }

  function setSlot(
    courtId: string,
    time: string,
    patch: Partial<LocalSlotState>,
  ) {
    setSlotsByCourt((prev) => {
      const courtMap = { ...(prev[courtId] ?? {}) };
      const current = courtMap[time] ?? emptyState();
      courtMap[time] = { ...current, ...patch };
      return { ...prev, [courtId]: courtMap };
    });
  }

  function clearSlot(courtId: string, time: string) {
    setSlotsByCourt((prev) => {
      const courtMap = { ...(prev[courtId] ?? {}) };
      courtMap[time] = emptyState();
      return { ...prev, [courtId]: courtMap };
    });
  }

  function setPriceRules(courtId: string, rules: PriceRule[]) {
    setPriceRulesByCourt((prev) => ({ ...prev, [courtId]: rules }));
  }

  // Copy one court's pricing schedule onto every court — the common case is a
  // club that prices all courts the same (mirrors the "apply hours to all"
  // pattern in the backlog).
  function applyPriceRulesToAll(sourceCourtId: string) {
    if (!query.data) return;
    setPriceRulesByCourt((prev) => {
      const src = prev[sourceCourtId] ?? [];
      const next = { ...prev };
      for (const c of query.data!.courts) next[c.id] = cloneRules(src);
      return next;
    });
    toast.success(t("timeSlots.priceRules.appliedToAll"));
  }

  const reset = () => {
    if (!original) return;
    const periodsCopy: PeriodsMap = {};
    for (const [k, v] of Object.entries(original.periods)) {
      periodsCopy[k] = v === null ? null : v.map((p) => ({ ...p }));
    }
    setPeriodsByCourt(periodsCopy);
    const slotsCopy: SlotMap = {};
    for (const [k, v] of Object.entries(original.slots)) {
      slotsCopy[k] = { ...v };
    }
    setSlotsByCourt(slotsCopy);
    const priceRulesCopy: PriceRulesMap = {};
    for (const [k, v] of Object.entries(original.priceRules)) {
      priceRulesCopy[k] = cloneRules(v);
    }
    setPriceRulesByCourt(priceRulesCopy);
    setAllowMemberOnly(original.allowMemberOnly);
    setAllowPrime(original.allowPrime);
  };

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-200 px-6 py-10 text-center text-sm text-neutral-500">
        {t("timeSlots.loadError")}
      </div>
    );
  }

  // Free clubs hide every price surface (matches court editor / booking flow).
  const showPricing = !query.data.isFree;
  const multipleCourts = query.data.courts.length > 1;
  // The two slot features are PRO-gated; below that tier the toggle is locked.
  const tier = query.data.subscriptionTier;
  const canMemberOnly = hasFeature(tier, "allowMemberOnlySlots");
  const canPrime = hasFeature(tier, "allowPrimeTimeSlots");
  const sportById = new Map(query.data.sports.map((s) => [s.id, s]));
  const clubStart = query.data.workingHoursStart;
  const clubEnd = query.data.workingHoursEnd;

  if (!subscriptionActive) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-200 px-6 py-10 text-center space-y-2">
        <Clock className="w-6 h-6 text-neutral-300 mx-auto" />
        <h2 className="font-heading font-semibold text-neutral-800">
          {t("timeSlots.featureOffTitle")}
        </h2>
        <p className="text-sm text-neutral-500 max-w-md mx-auto">
          {t("timeSlots.featureOffBody")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-neutral-200 p-4 space-y-3">
        <p className="text-xs font-medium text-neutral-600">
          {t("timeSlots.featuresHeading")}
        </p>
        <SlotFeatureToggle
          label={t("clubs.featuresDialog.allowMemberOnlySlots.label")}
          hint={t("clubs.featuresDialog.allowMemberOnlySlots.hint")}
          checked={allowMemberOnly}
          canUse={canMemberOnly}
          disabled={mutation.isPending}
          onCheckedChange={setAllowMemberOnly}
        />
        <SlotFeatureToggle
          label={t("clubs.featuresDialog.allowPrimeTimeSlots.label")}
          hint={t("clubs.featuresDialog.allowPrimeTimeSlots.hint")}
          checked={allowPrime}
          canUse={canPrime}
          disabled={mutation.isPending}
          onCheckedChange={setAllowPrime}
        />
      </div>

      <Legend showMemberOnly={allowMemberOnly} showPrime={allowPrime} />

      {query.data.courts.length === 0 && (
        <div className="rounded-xl border border-dashed border-neutral-200 px-6 py-10 text-center text-sm text-neutral-500">
          {t("timeSlots.noCourts")}
        </div>
      )}

      <div className="space-y-6">
        {query.data.courts.map((court) => {
          const sport = sportById.get(court.sportId);
          if (!sport) return null;
          const start = court.workingHoursStart ?? clubStart;
          const end = court.workingHoursEnd ?? clubEnd;
          const periods = periodsByCourt[court.id] ?? null;
          // Live regen of the slot grid from the current period schedule so
          // editing periods updates the chips below without a round-trip.
          const grid = generateSlotsFromPeriods({
            courtWorkingHoursStart: start,
            courtWorkingHoursEnd: end,
            periods,
            defaultDurationMinutes: sport.sessionDurationMinutes,
          });
          const courtSlots = slotsByCourt[court.id] ?? {};

          return (
            <section
              key={court.id}
              className="bg-white rounded-xl border border-neutral-200 p-4 space-y-4"
            >
              <header className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-heading font-semibold text-neutral-800">
                    {formatCourtLabel(court.name, t("timeSlots.courtPrefix"))}
                  </h3>
                  <p className="text-xs text-neutral-500">
                    {t(`sports.${sport.name}`)} ·{" "}
                    {t("timeSlots.workingHours", { start, end })}
                  </p>
                </div>
              </header>

              <PeriodEditor
                periods={periods}
                courtStart={start}
                courtEnd={end}
                defaultDuration={sport.sessionDurationMinutes}
                onChange={(p) => setPeriods(court.id, p)}
              />

              {grid.length === 0 ? (
                <div className="rounded-md bg-neutral-50 border border-dashed border-neutral-200 px-4 py-6 text-center text-sm text-neutral-500">
                  {t("timeSlots.periods.errors.noSlots")}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {grid.map((g) => {
                    const state = courtSlots[g.startTime] ?? emptyState();
                    return (
                      <SlotChip
                        key={g.startTime}
                        time={g.startTime}
                        durationMinutes={g.durationMinutes}
                        state={state}
                        allowMemberOnly={allowMemberOnly}
                        allowPrime={allowPrime}
                        onChange={(patch) =>
                          setSlot(court.id, g.startTime, patch)
                        }
                        onClear={() => clearSlot(court.id, g.startTime)}
                      />
                    );
                  })}
                </div>
              )}

              {showPricing && (
                <PriceRulesEditor
                  rules={priceRulesByCourt[court.id] ?? []}
                  courtPrice={court.pricePerSession}
                  showApplyToAll={multipleCourts}
                  onChange={(r) => setPriceRules(court.id, r)}
                  onApplyToAll={() => applyPriceRulesToAll(court.id)}
                />
              )}
            </section>
          );
        })}
      </div>

      {query.data.courts.length > 0 && (
        <div className="sticky bottom-0 -mx-4 md:mx-0 bg-white/95 backdrop-blur border-t border-neutral-200 px-4 py-3 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={reset}
            disabled={!isDirty || mutation.isPending}
          >
            {t("timeSlots.reset")}
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!isDirty || mutation.isPending || !subscriptionActive}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {mutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

interface LocalSlotRule {
  startTime: string;
  memberOnly: boolean;
  isPrime: boolean;
  isMaintenance: boolean;
}

function PeriodEditor({
  periods,
  courtStart,
  courtEnd,
  defaultDuration,
  onChange,
}: {
  periods: SlotPeriod[] | null;
  courtStart: string;
  courtEnd: string;
  defaultDuration: number;
  onChange: (periods: SlotPeriod[] | null) => void;
}) {
  const { t } = useTranslation();

  if (periods === null) {
    // "Default schedule" — read-only summary with a "Customize" button that
    // promotes to an editable single-period state seeded from the default.
    return (
      <div className="flex items-center gap-3 rounded-md bg-neutral-50 border border-neutral-200 px-3 py-2">
        <Clock className="w-4 h-4 text-neutral-400" />
        <div className="text-sm text-neutral-600 flex-1">
          <span className="font-medium text-neutral-700">
            {t("timeSlots.periods.default")}
          </span>{" "}
          <span className="text-neutral-500">
            ({t("timeSlots.periods.minutes", { count: defaultDuration })})
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onChange([
              { startTime: courtStart, durationMinutes: defaultDuration },
            ])
          }
        >
          {t("timeSlots.periods.customize")}
        </Button>
      </div>
    );
  }

  const setRow = (i: number, patch: Partial<SlotPeriod>) => {
    const next = periods.map((p, j) => (j === i ? { ...p, ...patch } : p));
    onChange(next);
  };
  const removeRow = (i: number) => {
    const next = periods.filter((_, j) => j !== i);
    onChange(next.length === 0 ? null : next);
  };
  const addRow = () => {
    const last = periods[periods.length - 1];
    // Default the new period start to the last period's start + duration so
    // it sequences cleanly. Caller can adjust.
    const [lh, lm] = last.startTime.split(":").map(Number);
    const nextStartMin = lh * 60 + lm + last.durationMinutes;
    const hh = Math.floor(nextStartMin / 60)
      .toString()
      .padStart(2, "0");
    const mm = (nextStartMin % 60).toString().padStart(2, "0");
    const nextStart = `${hh}:${mm}`;
    onChange([
      ...periods,
      { startTime: nextStart, durationMinutes: defaultDuration },
    ]);
  };

  return (
    <div className="rounded-md bg-neutral-50 border border-neutral-200 px-3 py-3 space-y-2">
      <p className="text-xs font-medium text-neutral-600">
        {t("timeSlots.periods.heading")}
      </p>
      <p className="text-xs text-neutral-500">
        {t("timeSlots.periods.description")}
      </p>
      <div className="space-y-2 pt-1">
        {periods.map((p, i) => (
          <div key={i} className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-neutral-500 min-w-fit">
              {t("timeSlots.periods.startTimeLabel")}
            </label>
            <input
              type="time"
              value={p.startTime}
              min={courtStart}
              max={courtEnd}
              onChange={(e) => setRow(i, { startTime: e.target.value })}
              className="font-mono text-sm border border-neutral-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <label className="text-xs text-neutral-500 min-w-fit ml-2">
              {t("timeSlots.periods.durationLabel")}
            </label>
            <input
              type="number"
              min={15}
              max={480}
              step={15}
              value={p.durationMinutes}
              onChange={(e) =>
                setRow(i, {
                  durationMinutes: Math.max(15, Number(e.target.value) || 15),
                })
              }
              className="font-mono text-sm border border-neutral-200 rounded px-2 py-1 w-20 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <span className="text-xs text-neutral-500">min</span>
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="ml-auto text-neutral-400 hover:text-red-500 p-1 cursor-pointer"
              aria-label={t("timeSlots.periods.remove")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between pt-1">
        <Button variant="outline" size="sm" onClick={addRow}>
          <Plus className="w-3.5 h-3.5 mr-1" />
          {t("timeSlots.periods.addPeriod")}
        </Button>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs text-neutral-500 hover:text-neutral-700 cursor-pointer"
        >
          {t("timeSlots.periods.default")}
        </button>
      </div>
    </div>
  );
}

function Legend({
  showMemberOnly,
  showPrime,
}: {
  showMemberOnly: boolean;
  showPrime: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500">
      <span className="font-medium text-neutral-600">
        {t("timeSlots.legend.label")}
      </span>
      {showMemberOnly && (
        <LegendItem className={CHIP_CLASSES.memberOnly}>
          {t("timeSlots.legend.memberOnly")}
        </LegendItem>
      )}
      {showPrime && (
        <LegendItem className={CHIP_CLASSES.prime}>
          {t("timeSlots.legend.prime")}
        </LegendItem>
      )}
      {showMemberOnly && showPrime && (
        <LegendItem className={CHIP_CLASSES.both}>
          {t("timeSlots.legend.both")}
        </LegendItem>
      )}
      <LegendItem className={CHIP_CLASSES.maintenance}>
        {t("timeSlots.legend.maintenance")}
      </LegendItem>
    </div>
  );
}

// One slot-feature toggle (Member-only / Prime-time). PRO-gated: when the
// club's tier is too low and the feature isn't already on, the switch is locked
// and a "Pro" hint shows (mirrors the booking-features tier UX).
function SlotFeatureToggle({
  label,
  hint,
  checked,
  canUse,
  disabled,
  onCheckedChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  canUse: boolean;
  disabled: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const locked = !canUse && !checked;
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-0.5">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-neutral-800">{label}</p>
          {!canUse && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-700">
              {t("timeSlots.proBadge")}
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-500">{hint}</p>
        {locked && (
          <p className="text-xs text-amber-700">
            {t("timeSlots.proFeatureHint")}
          </p>
        )}
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled || locked}
      />
    </div>
  );
}

function LegendItem({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${className}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
}

function SlotChip({
  time,
  durationMinutes,
  state,
  allowMemberOnly,
  allowPrime,
  onChange,
  onClear,
}: {
  time: string;
  durationMinutes: number;
  state: LocalSlotState;
  allowMemberOnly: boolean;
  allowPrime: boolean;
  onChange: (patch: Partial<LocalSlotState>) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const baseState = chipBaseStateFor(state);
  const active = baseState !== "off";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("timeSlots.editSlot", { time })}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors cursor-pointer ${CHIP_CLASSES[baseState]}`}
        >
          <span className="font-mono">{time}</span>
          {state.isPrime && !state.isMaintenance && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white/60 border border-current/30">
              {t("timeSlots.primeBadge")}
            </span>
          )}
          {state.isMaintenance && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white/60 border border-current/30">
              {t("timeSlots.maintenanceBadge")}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-mono text-sm font-semibold text-neutral-800">
              {time}
            </span>
            <span className="text-xs text-neutral-500 ml-2">
              {t("timeSlots.periods.minutes", { count: durationMinutes })}
            </span>
          </div>
          {active && (
            <button
              type="button"
              onClick={onClear}
              className="text-xs text-neutral-500 hover:text-neutral-700 cursor-pointer"
            >
              {t("timeSlots.clear")}
            </button>
          )}
        </div>

        {allowMemberOnly && !state.isMaintenance && (
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox
              checked={state.memberOnly}
              onCheckedChange={(v) => onChange({ memberOnly: !!v })}
              className="mt-0.5"
            />
            <span className="text-sm text-neutral-700 leading-tight">
              {t("timeSlots.memberOnlyLabel")}
            </span>
          </label>
        )}

        {allowPrime && !state.isMaintenance && (
          <label className="flex items-start gap-2 cursor-pointer">
            <Checkbox
              checked={state.isPrime}
              onCheckedChange={(v) => onChange({ isPrime: !!v })}
              className="mt-0.5"
            />
            <span className="text-sm text-neutral-700 leading-tight">
              {t("timeSlots.primeLabel")}
            </span>
          </label>
        )}

        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox
            checked={state.isMaintenance}
            onCheckedChange={(v) => onChange({ isMaintenance: !!v })}
            className="mt-0.5"
          />
          <span className="text-sm text-neutral-700 leading-tight">
            {t("timeSlots.maintenanceLabel")}
          </span>
        </label>
      </PopoverContent>
    </Popover>
  );
}

// Day/time pricing schedule for one court. Each rule charges `price` on the
// selected weekdays within [from, to). The booking date picks the rule at
// booking time (server-side resolveSlotPrice); the per-slot override on a chip
// still wins over any rule.
function PriceRulesEditor({
  rules,
  courtPrice,
  showApplyToAll,
  onChange,
  onApplyToAll,
}: {
  rules: PriceRule[];
  courtPrice: string;
  showApplyToAll: boolean;
  onChange: (rules: PriceRule[]) => void;
  onApplyToAll: () => void;
}) {
  const { t } = useTranslation();
  const dayShort = t("timeSlots.priceRules.dayShort", {
    returnObjects: true,
  }) as unknown as string[];
  const overlap = priceRulesOverlap(rules);

  const setRule = (i: number, patch: Partial<PriceRule>) =>
    onChange(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const toggleDay = (i: number, day: number) => {
    const r = rules[i];
    const days = r.days.includes(day)
      ? r.days.filter((d) => d !== day)
      : [...r.days, day].sort((a, b) => a - b);
    setRule(i, { days });
  };
  const removeRule = (i: number) => onChange(rules.filter((_, j) => j !== i));
  const addRule = () =>
    onChange([
      ...rules,
      {
        days: [...PRESET_DAYS.weekdays],
        startTime: "08:00",
        endTime: "16:00",
        price: "",
      },
    ]);

  return (
    <div className="rounded-md bg-neutral-50 border border-neutral-200 px-3 py-3 space-y-3">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-xs font-medium text-neutral-600 inline-flex items-center gap-1">
            <Euro className="w-3.5 h-3.5" />
            {t("timeSlots.priceRules.heading")}
          </p>
          <p className="text-xs text-neutral-500 mt-0.5">
            {t("timeSlots.priceRules.description", { price: courtPrice })}
          </p>
        </div>
        {showApplyToAll && rules.length > 0 && (
          <Button variant="outline" size="sm" onClick={onApplyToAll}>
            <Copy className="w-3.5 h-3.5 mr-1" />
            {t("timeSlots.priceRules.applyToAll")}
          </Button>
        )}
      </div>

      {rules.length === 0 ? (
        <p className="text-xs text-neutral-500 italic">
          {t("timeSlots.priceRules.empty", { price: courtPrice })}
        </p>
      ) : (
        <div className="space-y-3">
          {rules.map((rule, i) => (
            <div
              key={i}
              className="rounded-md bg-white border border-neutral-200 p-3 space-y-2"
            >
              <div className="flex items-center gap-1.5 flex-wrap">
                {DAY_ORDER.map((d) => {
                  const on = rule.days.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(i, d)}
                      className={`px-2 py-1 rounded-md text-xs font-medium border cursor-pointer transition-colors ${
                        on
                          ? "bg-primary-600 border-primary-600 text-white"
                          : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300"
                      }`}
                    >
                      {dayShort[d]}
                    </button>
                  );
                })}
                <div className="flex items-center gap-2 ml-1">
                  {Object.keys(PRESET_DAYS).map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() =>
                        setRule(i, { days: [...PRESET_DAYS[key]] })
                      }
                      className="text-[11px] text-primary-700 hover:underline cursor-pointer"
                    >
                      {t(`timeSlots.priceRules.presets.${key}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-xs text-neutral-500">
                  {t("timeSlots.priceRules.fromLabel")}
                </label>
                <input
                  type="time"
                  value={rule.startTime}
                  onChange={(e) => setRule(i, { startTime: e.target.value })}
                  className="font-mono text-sm border border-neutral-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <label className="text-xs text-neutral-500">
                  {t("timeSlots.priceRules.toLabel")}
                </label>
                <input
                  type="time"
                  value={rule.endTime}
                  onChange={(e) => setRule(i, { endTime: e.target.value })}
                  className="font-mono text-sm border border-neutral-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <div className="flex items-center gap-1.5 ml-1">
                  <Euro className="w-4 h-4 text-neutral-400" />
                  <input
                    type="text"
                    inputMode="decimal"
                    value={rule.price}
                    placeholder={courtPrice}
                    onChange={(e) => {
                      const raw = e.target.value.trim();
                      if (raw === "" || /^\d+(\.\d{0,2})?$/.test(raw)) {
                        setRule(i, { price: raw });
                      }
                    }}
                    className="font-mono text-sm border border-neutral-200 rounded px-2 py-1 w-24 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <span className="text-xs text-neutral-500">RSD</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeRule(i)}
                  className="ml-auto text-neutral-400 hover:text-red-500 p-1 cursor-pointer"
                  aria-label={t("timeSlots.priceRules.remove")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {overlap && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          {t("timeSlots.priceRules.overlapWarning")}
        </p>
      )}

      <Button variant="outline" size="sm" onClick={addRule}>
        <Plus className="w-3.5 h-3.5 mr-1" />
        {t("timeSlots.priceRules.addRule")}
      </Button>
    </div>
  );
}
